import { createAsyncThunk, createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { history } from '../../utils/history';
import { transformUserObj, getCustomerBase } from '../../utils/commonFunctions';
import { FacilityUserRole } from '../../services/apiTypes';

export interface User {
    idToken?: string;
    refreshToken?: string;
    accessToken?: string;
    customerApiBase?: string;
    apiURL?: string;
    permissions?: FacilityUserRole[];
    [key: string]: any;
}

export interface DeviceAttribute {
    Name: string,
    Value: string
}
export interface Device {
    DeviceAttributes?: DeviceAttribute[],
    DeviceCreateDate?: number,
    DeviceKey?: string,
    DeviceLastAuthenticatedDate?: number,
    DeviceLastModifiedDate?: number
}

export interface AuthState {
    user: User | null;
    error: string | null;
    loading: boolean;
    authChallengeData: any | null;
    mfaChallengeData: MFAChallengeData | null;
    mfaSetupData: MFASetupData | null;
    mfaSettings: MFASettings | null;
    devices: Device[] | null;
}


export interface MFASettings {
    PreferredMFASetting?: string;
    UserMFASettingList?: ('SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP' | 'SMS_MFA')[];
    ConfiguredUserAuthFactors?: ("EMAIL_OTP" | "PASSWORD" | "SMS_OTP" | "WEB_AUTHN")[];
    Username?: string;
}

export interface MFAChallengeData {
    ChallengeName: ('EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA' | 'MFA_SETUP' | 'NEW_PASSWORD_REQUIRED' | 'CUSTOM_CHALLENGE');
    Session: string;
    ChallengeParameters?: any;
    username: string;
}

export interface MFASetupData {
    SecretCode?: string;
    Session?: string;
}

export interface LoginCredentials {
    username: string;
    password: string;
}

export interface ForgotPasswordCredentials {
    username: string;
}

export interface RecoverPasswordCredentials {
    username: string;
    password: string;
    code: string;
}

export interface ChallengeCredentials {
    username: string;
    password: string;
    sessionToken: string;
}

export interface RefreshTokenParams {
    userInfo: User;
}

export interface SSOCallbackParams {
    code: string;
    state: string; // Required - auth API encodes tenantId in OAuth state
}

export interface MFAChallengeCredentials {
    username: string;
    sessionToken: string;
    emailCode: string;
}

export interface MFASetupCredentials {
    username: string;
    accessToken: string;
}

export interface MFAVerificationCredentials {
    username: string;
    accessToken: string;
    userCode: string;
}

export interface EnableMFACredentials {
    username: string;
    enableEmailMFA: boolean;
}

function createInitialState(): AuthState {
    const user = localStorage.getItem('user') || "";
    return {
        // initialize state from local storage to enable user to stay logged in
        user: user ? JSON.parse(user) : null,
        error: null,
        loading: false,
        authChallengeData: null,
        mfaChallengeData: null,
        mfaSetupData: null,
        mfaSettings: null,
        devices: null
    };
}

function createReducers() {
    return {
        logout,
        clearMFAChallenge,
        clearMFASetup,
        clearError
    };

    function logout(state: AuthState): void {
        state.user = null;
        state.authChallengeData = null;
        state.mfaChallengeData = null;
        state.mfaSetupData = null;
        state.error = null;
        localStorage.removeItem('user');
        history?.navigate('/');
    }

    function clearMFAChallenge(state: AuthState): void {
        state.mfaChallengeData = null;
    }

    function clearMFASetup(state: AuthState): void {
        state.mfaSetupData = null;
    }

    function clearError(state: AuthState): void {
        state.error = null;
    }
}

const name = 'auth';
const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();
const extraReducers = createExtraReducers();
const slice = createSlice({ name, initialState, reducers, extraReducers });

function createExtraActions() {
    console.log('NODE_ENV is ', process.env.NODE_ENV);
    console.log('AUTH URL is ', process.env.REACT_APP_AUTH_URL);
    const baseUrl = `${process.env.REACT_APP_AUTH_URL}`;

    return {
        login: login(),
        forgotPassword: forgotPassword(),
        recoverPassword: recoverPassword(),
        refreshToken: refreshToken(),
        respondToChallenge: respondToChallenge(),
        // New MFA actions
        respondToEmailMFA: respondToEmailMFA(),
        enableEmailMFA: enableEmailMFA(),
        // Additional MFA actions
        setupTOTPMFA: setupTOTPMFA(),
        verifyTOTPMFA: verifyTOTPMFA(),
        respondToTOTPMFA: respondToTOTPMFA(),
        enableMFA: enableMFA(),
        listDevices: listDevices(),
        getMFASettings: getMFASettings(),
        confirmDevice: confirmDevice(),
        // SSO actions
        initiateSSOLogin: initiateSSOLogin(),
        handleSSOCallback: handleSSOCallback()
    };

    function login() {
        return createAsyncThunk<User | MFAChallengeData, LoginCredentials>(`${name}/login`, async ({ username, password }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('password', password);
            const body = new URLSearchParams(form as any);
            
            try {
                const response = await fetch(baseUrl + '/login', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: body,
                });
                
                const data = await response.json();
                
                // Handle MFA challenge
                if (data?.ChallengeName === 'EMAIL_OTP') {
                    return {
                        ...data,
                        username // Add username for MFA handling
                    } as MFAChallengeData;
                }

                if (data?.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
                    return {
                        ...data,
                        username // Add username for MFA handling
                    } as MFAChallengeData;
                }

                if (data?.ChallengeName === 'SMS_MFA') {
                    return {
                        ...data,
                        username // Add username for MFA handling
                    } as MFAChallengeData;
                }

                if (data?.ChallengeName === 'MFA_SETUP') {
                    return {
                        ...data,
                        username // Add username for MFA handling
                    } as MFAChallengeData;
                }


                // Handle password challenge
                if (data?.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                    return data;
                }
                
                // Handle successful login
                if (data?.idToken) {
                    return data;
                }
                
                throw Error(data.message || 'Login failed');
            } catch (e) {
                throw e;
            }
        });
    }

    function forgotPassword() {
        return createAsyncThunk<any, ForgotPasswordCredentials>(`${name}/forgot-password`, async ({ username }) => {
            const form = new FormData();
            form.append('username', username);
            const body = new URLSearchParams(form as any);
            
            const response = await fetch(baseUrl + '/forgot-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }

    function recoverPassword() {
        return createAsyncThunk<any, RecoverPasswordCredentials>(`${name}/recover-password`, async ({ username, password, code }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('password', password);
            form.append('confirmationCode', code);
            const body = new URLSearchParams(form as any)
            
            const response = await fetch(baseUrl + '/recover-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return response;
        });
    }

    function refreshToken() {
        return createAsyncThunk<User, RefreshTokenParams>(`${name}/refresh-token`, async ({ userInfo }) => {
            return userInfo;
        });
    }

    function respondToChallenge() {
        return createAsyncThunk<User, ChallengeCredentials>(`${name}/respond-to-challenge`, async ({ username, password, sessionToken }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('password', password);
            form.append('sessionToken', sessionToken);
            const body = new URLSearchParams(form as any)
            
            const response = await fetch(baseUrl + '/confirm-new-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            
                    // Create initial user object with authentication tokens
            const userObj = {
                idToken: data?.AuthenticationResult?.IdToken,
                accessToken: data?.AuthenticationResult?.AccessToken,
                apiURL: data?.apiURL,
                socketURL: data?.socketURL,
                refreshToken: data?.AuthenticationResult?.AccessToken,
                permissions: data?.permissions || [],
                customerApiBase: data?.apiURL ? getCustomerBase(data?.apiURL) : undefined
            };
            
            console.log('Created initial user object for password challenge:', userObj);
            
            // Note: The password challenge response doesn't include user permissions
            // The user object will have empty permissions initially
            // The user should refresh the page or re-login to get full permissions
            // This is a limitation of the current API design
            
            localStorage.setItem('user', JSON.stringify(userObj));
            return userObj;
        });
    }

    // New MFA functions
    function getMFASettings() {
        return createAsyncThunk<any, { username: string; accessToken: string }>(`${name}/get-mfa-settings`, async ({ username, accessToken }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('accessToken', accessToken);
            const body = new URLSearchParams(form as any);
            
            const response = await fetch(baseUrl + '/get-mfa-settings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }

    function listDevices() {
        return createAsyncThunk<any, { username: string; accessToken: string }>(`${name}/list-devices`, async ({ username, accessToken }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('accessToken', accessToken);
            const body = new URLSearchParams(form as any);

            const response = await fetch(baseUrl + '/list-devices', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });

            const data: { Devices: Device[], PaginationToken?: string } = await response.json();
            if (response.status === 400) {
                throw Error('Failed to list devices');
            }
            return data;
        });
    }

    function respondToEmailMFA() {
        return createAsyncThunk<User, MFAChallengeCredentials>(`${name}/respond-to-email-mfa`, async ({ username, sessionToken, emailCode }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('sessionToken', sessionToken);
            form.append('emailCode', emailCode);
            const body = new URLSearchParams(form as any);
            
            const response = await fetch(baseUrl + '/respond-email-mfa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (data?.idToken) {
                const transformedUserObj = transformUserObj(data);
                localStorage.setItem('user', JSON.stringify(transformedUserObj));
                return transformedUserObj;
            } else {
                throw Error(data.message || 'MFA verification failed');
            }
        });
    }

    function enableEmailMFA() {
        return createAsyncThunk<any, EnableMFACredentials>(`${name}/enable-email-mfa`, async ({ username, enableEmailMFA }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('enableEmailMFA', enableEmailMFA.toString());
            const body = new URLSearchParams(form as any);
            
            const response = await fetch(baseUrl + '/enable-email-mfa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }

    // Additional MFA functions
    function setupTOTPMFA() {
        return createAsyncThunk<any, { username: string; accessToken: string }>(`${name}/setup-totp-mfa`, async ({ username, accessToken }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('accessToken', accessToken);
            const body = new URLSearchParams(form as any);
            
            const response = await fetch(baseUrl + '/setup-totp-mfa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }

    function verifyTOTPMFA() {
        return createAsyncThunk<any, { username: string; accessToken: string; code: string }>(`${name}/verify-totp-mfa`, async ({ username, accessToken, code }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('accessToken', accessToken);
            form.append('code', code);
            const body = new URLSearchParams(form as any);
            
            const response = await fetch(baseUrl + '/verify-totp-mfa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }

    function confirmDevice() {
        return createAsyncThunk<any, { username: string; accessToken: string; deviceId: string }>(`${name}/confirm-device`, async ({ username, accessToken, deviceId }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('accessToken', accessToken);
            form.append('deviceId', deviceId);
            const body = new URLSearchParams(form as any);

            const response = await fetch(baseUrl + '/confirm-device', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });

            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }

    function initiateSSOLogin() {
        return createAsyncThunk<void, { tenantId?: string; domain?: string }>(
            `${name}/initiate-sso-login`,
            async ({ tenantId, domain }) => {
                const callbackUrl = `${window.location.origin}/auth/callback`;
                const params = new URLSearchParams({ redirect_uri: callbackUrl });
                if (domain?.trim()) {
                    params.set('domain', domain.trim());
                } else if (tenantId?.trim()) {
                    params.set('tenantId', tenantId.trim());
                }
                const response = await fetch(`${baseUrl}/sso/login?${params}`);
                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw Error(data.message || `SSO login failed: ${response.status}`);
                }
                const data = await response.json();
                const redirectUrl = data?.redirectUrl;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }
                throw Error('No redirect URL received from SSO endpoint');
            }
        );
    }

    function handleSSOCallback() {
        return createAsyncThunk<User, SSOCallbackParams>(
            `${name}/handle-sso-callback`,
            async ({ code, state }) => {
                const redirectUri = `${window.location.origin}/auth/callback`;
                const form = new FormData();
                form.append('code', code);
                form.append('redirect_uri', redirectUri);
                form.append('state', state);
                const body = new URLSearchParams(form as any);

                const response = await fetch(`${baseUrl}/sso/callback`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body,
                });

                const data = await response.json();
                if (!response.ok) {
                    throw Error(data.message || 'SSO callback failed');
                }

                if (data?.idToken) {
                    const transformedUserObj = transformUserObj(data);
                    localStorage.setItem('user', JSON.stringify(transformedUserObj));
                    return transformedUserObj;
                }

                throw Error(data.message || 'SSO callback did not return tokens');
            }
        );
    }

    function enableMFA() {
        return createAsyncThunk<any, { username: string; accessToken: string; enableSMS: boolean; enableTOTP: boolean; preferredMFA?: string }>(`${name}/enable-mfa`, async ({ username, accessToken, enableSMS, enableTOTP, preferredMFA }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('accessToken', accessToken);
            form.append('enableSMS', enableSMS.toString());
            form.append('enableTOTP', enableTOTP.toString());
            if (preferredMFA) {
                form.append('preferredMFA', preferredMFA);
            }
            const body = new URLSearchParams(form as any);
            
            const response = await fetch(baseUrl + '/enable-mfa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }

    function respondToTOTPMFA() {
        return createAsyncThunk<any, { username: string; sessionToken: string; code: string }>(`${name}/respond-to-totp-mfa`, async ({ username, sessionToken, code }) => {
            const form = new FormData();
            form.append('username', username);
            form.append('sessionToken', sessionToken);
            form.append('code', code);
            const body = new URLSearchParams(form as any);

            const response = await fetch(baseUrl + '/respond-totp-mfa', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body,
            });
            const data = await response.json();
            if (response.status === 400) {
                throw Error(data.message);
            }
            return data;
        });
    }
}

//sets state
function createExtraReducers() {
    return (builder: ActionReducerMapBuilder<AuthState>) => {
        login();
        resetPassword();
        recoverPassword();
        refreshToken();
        respondToChallenge();
        // New MFA reducers
        respondToEmailMFA();
        enableEmailMFA();
        // Additional MFA reducers
        setupTOTPMFA();
        verifyTOTPMFA();
        enableMFA();
        listDevices();
        getMFASettings()
        confirmDevice();
        respondToTOTPMFA();
        initiateSSOLogin();
        handleSSOCallback();

        function login() {
            const { pending, fulfilled, rejected } = extraActions.login;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<User | MFAChallengeData>) => {
                    let authResponse = action.payload;
                    
                                    // Handle MFA challenge
                if ('ChallengeName' in authResponse && 
                    (authResponse.ChallengeName === 'EMAIL_OTP')) {
                    state.mfaChallengeData = authResponse as MFAChallengeData;
                    state.loading = false;
                    state.error = "Please enter the verification code sent to your email.";
                    return;
                }
                    if ('ChallengeName' in authResponse && 
                        (authResponse.ChallengeName === 'SOFTWARE_TOKEN_MFA')) {
                        state.mfaChallengeData = authResponse as MFAChallengeData;
                        state.loading = false;
                        state.error = "Please enter the verification code sent to your authenticator app.";
                        return;
                    }
                    if ('ChallengeName' in authResponse && 
                        (authResponse.ChallengeName === 'SMS_MFA')) {
                        state.mfaChallengeData = authResponse as MFAChallengeData;
                        state.loading = false;
                        state.error = "Please enter the verification code sent to your phone.";
                        return;
                    }
                    if ('ChallengeName' in authResponse && 
                        (authResponse.ChallengeName === 'MFA_SETUP')) {
                        state.mfaChallengeData = authResponse as MFAChallengeData;
                        state.loading = false;
                        state.error = "Please enter the verification code sent to your phone.";
                        return;
                    }
                    // Handle password challenge
                    if ('Session' in authResponse && authResponse.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                        state.authChallengeData = authResponse;
                        state.error = "Please create a new password. Passwords must be at least 12 characters long and include upper and lowercase letters, numbers, and special characters.";
                        state.loading = false;
                        return;
                    }
                    if ('Session' in authResponse && authResponse.ChallengeName === 'CUSTOM_CHALLENGE') {
                        state.authChallengeData = authResponse;
                        state.error = "Please enter the verification code sent to your phone.";
                        state.loading = false;
                        return;
                    }
                    
                    // Handle successful login
                    if ('idToken' in authResponse && authResponse.idToken) {
                        const user = authResponse as User;
                        const transformedUserObj = transformUserObj(user);
                        localStorage.setItem('user', JSON.stringify(transformedUserObj));
                        state.user = transformedUserObj;
                        state.loading = false;
                        
                        // get return url from location state or default to home page
                        const { to } = history.location.state || { to: { pathname: '/' } };
                        history.navigate(to);
                        return;
                    }
                    
                    state.error = (authResponse as any).message;
                    state.loading = false;
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'An error occurred during login';
                    state.loading = false;
                });
        }

        function refreshToken() {
            const { pending, fulfilled, rejected } = extraActions.refreshToken;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<User>) => {
                    const response = action.payload;
                    const user = transformUserObj(response);
                    localStorage.setItem('user', JSON.stringify(user));
                    state.user = user;
                    state.loading = false;
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'An error occurred refreshing the token';
                    state.loading = false;
                });
        }

        function resetPassword() {
            const { pending, fulfilled, rejected } = extraActions.forgotPassword;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<any>) => {
                    state.loading = false;
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'An error occurred resetting password';
                    state.loading = false;
                });
        }

        function recoverPassword() {
            const { pending, fulfilled, rejected } = extraActions.recoverPassword;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<any>) => {
                    state.loading = false;
                    // store user details and basic auth data in local storage to keep user logged in between page refreshes
                    const { to } = history.location.state || { to: { pathname: '/' } };
                    history.navigate(to);
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action?.error?.message ?? 'An error occurred recovering password';
                    state.loading = false;
                });
        }

        function respondToChallenge() {
            const { pending, fulfilled, rejected } = extraActions.respondToChallenge;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<User>) => {
                    console.log('respondToChallenge fulfilled with user:', action.payload);
                    const user = action.payload;
                    const transformedUserObj = transformUserObj(user);
                    console.log('Transformed user object:', transformedUserObj);

                    localStorage.setItem('user', JSON.stringify(transformedUserObj));
                    state.user = transformedUserObj;
                    state.loading = false;
                    state.authChallengeData = null;
                    // get return url from location state or default to home page
                    const { to } = history.location.state || { to: { pathname: '/' } };
                    console.log('Navigating to:', to);
                    history.navigate(to);
                })
                .addCase(rejected, (state: AuthState, action) => {
                    console.error('respondToChallenge rejected:', action.error);
                    state.error = action?.error?.message ?? 'An error occurred responding to challenge';
                    state.loading = false;
                });
        }

        function respondToEmailMFA() {
            const { pending, fulfilled, rejected } = extraActions.respondToEmailMFA;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<User>) => {
                    const user = action.payload;
                    const transformedUserObj = transformUserObj(user);

                    localStorage.setItem('user', JSON.stringify(transformedUserObj));
                    state.user = transformedUserObj;
                    state.mfaChallengeData = null;

                    state.loading = false;
                    // get return url from location state or default to home page
                    const { to } = history.location.state || { to: { pathname: '/' } };
                    history.navigate(to);
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action?.error?.message ?? 'MFA verification failed';
                    state.loading = false;
                });
        }

        function respondToTOTPMFA() {
            const { pending, fulfilled, rejected } = extraActions.respondToTOTPMFA;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<{}>) => {
                    const user = action.payload;
                    const transformedUserObj = transformUserObj(user);

                    localStorage.setItem('user', JSON.stringify(transformedUserObj));
                    state.user = transformedUserObj;
                    state.mfaChallengeData = null;

                    state.loading = false;
                    // get return url from location state or default to home page
                    const { to } = history.location.state || { to: { pathname: '/' } };
                    history.navigate(to);
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action?.error?.message ?? 'TOTP MFA verification failed';
                    state.loading = false;
                });
        }

        function enableEmailMFA() {
            const { pending, fulfilled, rejected } = extraActions.enableEmailMFA;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<any>) => {
                    state.loading = false;
                    // Email MFA enabled/disabled successfully
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action?.error?.message ?? 'Failed to update MFA settings';
                    state.loading = false;
                });
        }

        function listDevices() {
            const { pending, fulfilled, rejected } = extraActions.listDevices;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<{ Devices: Device[], PaginationToken?: string }>) => {
                    state.devices = action.payload.Devices;
                    state.loading = false;
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'Failed to setup MFA';
                    state.loading = false;
                });
        }

        function getMFASettings() {
            const { pending, fulfilled, rejected } = extraActions.getMFASettings;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<MFASettings>) => {
                    state.loading = false;
                    state.mfaSettings = action.payload;
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'MFA setup verification failed';
                    state.loading = false;
                });
        }

        // Additional MFA reducers
        function setupTOTPMFA() {
            const { pending, fulfilled, rejected } = extraActions.setupTOTPMFA;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<any>) => {
                    state.loading = false;
                    // TOTP MFA setup successfully
                    state.mfaSetupData = action.payload;
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'Failed to setup TOTP MFA';
                    state.loading = false;
                });
        }

        function verifyTOTPMFA() {
            const { pending, fulfilled, rejected } = extraActions.verifyTOTPMFA;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<{ Status: string, $metadata: any }>) => {
                    state.loading = false;
                    const response = action.payload;
                    if (response.Status === 'SUCCESS') {
                        state.mfaSetupData = null;
                        state.mfaSettings = {
                            UserMFASettingList: ['SOFTWARE_TOKEN_MFA'],
                        }              
                       
                    }
                    else {
                       console.log('response', response);
                    }
                    // MFA setup verified successfully
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'TOTP MFA verification failed';
                    state.loading = false;
                });
        }

        function enableMFA() {
            const { pending, fulfilled, rejected } = extraActions.enableMFA;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<any>) => {
                    state.loading = false;
                    // MFA enabled/disabled successfully
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action?.error?.message ?? 'Failed to update MFA settings';
                    state.loading = false;
                });
        }

        function confirmDevice() {
            const { pending, fulfilled, rejected } = extraActions.confirmDevice;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<any>) => {
                    state.loading = false;
                    // Device confirmed successfully

                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'Failed to confirm device';
                    state.loading = false;
                });
        }

        function initiateSSOLogin() {
            const { pending, rejected } = extraActions.initiateSSOLogin;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'SSO login failed';
                    state.loading = false;
                });
        }

        function handleSSOCallback() {
            const { pending, fulfilled, rejected } = extraActions.handleSSOCallback;
            builder
                .addCase(pending, (state: AuthState) => {
                    state.error = null;
                    state.loading = true;
                })
                .addCase(fulfilled, (state: AuthState, action: PayloadAction<User>) => {
                    const user = action.payload;
                    const transformedUserObj = transformUserObj(user);
                    localStorage.setItem('user', JSON.stringify(transformedUserObj));
                    state.user = transformedUserObj;
                    state.loading = false;
                    const { to } = history.location.state || { to: { pathname: '/' } };
                    history.navigate(to);
                })
                .addCase(rejected, (state: AuthState, action) => {
                    state.error = action.error?.message ?? 'SSO callback failed';
                    state.loading = false;
                });
        }
    };
}

export const authActions = { ...slice.actions, ...extraActions };
export const authReducer = slice.reducer;
